import { useMemo } from "react";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import type { PermissionKey } from "@shared/permissions";

/**
 * What the signed-in user may do here and now (FR-13).
 *
 * The permission list comes from the server's own role → permission resolution
 * (`/api/v2/me` → `instances[].permissions` for the selected instance, plus
 * `platformPermissions` for what needs no instance at all); the client never
 * derives it from the role name, so the UI and the route guard can only agree or
 * both be wrong, never disagree. Hiding a control is a courtesy — the route
 * check is the control — but a courtesy that shows the wrong thing is worse than
 * none.
 *
 * The two lists are a UNION rather than a choice. A platform-scope permission is
 * resolved from the role assignments and is true whether or not an instance is
 * selected, so dropping it while inside an instance would hide the platform
 * screens from the nav, and dropping it outside one would leave a PlatformAdmin
 * on a fresh deployment with no permission at all and no way out of the picker
 * (FR-20).
 */
export interface PermissionSet {
  /** True when the user holds every permission listed. */
  can: (...permissions: PermissionKey[]) => boolean;
  /** True when the user holds at least one of the permissions listed. */
  canAny: (...permissions: PermissionKey[]) => boolean;
  /** The raw set, for a caller that needs to enumerate. */
  permissions: ReadonlySet<PermissionKey>;
  /**
   * True when the user holds a platform-scope permission, whatever the instance
   * selection is. The instance picker offers its "Platform administration"
   * action on this, and the shell lets the platform screens render without an
   * instance on it.
   */
  hasPlatformScope: boolean;
  /** False until `/api/v2/me` has answered — render nothing rather than guess. */
  isResolved: boolean;
}

export function usePermissions(): PermissionSet {
  const { selectedInstance, me } = useTenantInstance();

  return useMemo(() => {
    const permissions = new Set<PermissionKey>([
      ...(selectedInstance?.permissions ?? []),
      ...(me?.platformPermissions ?? []),
    ]);
    return {
      permissions,
      hasPlatformScope: (me?.platformPermissions ?? []).length > 0,
      isResolved: Boolean(me),
      can: (...keys: PermissionKey[]) => keys.every((k) => permissions.has(k)),
      canAny: (...keys: PermissionKey[]) => keys.some((k) => permissions.has(k)),
    };
  }, [selectedInstance, me]);
}
